import React, { useEffect, useMemo, useState } from "react";
import { sortRows } from "../issueAnalyticsHelpers";

// Ranked list of agents or clients with search, sort, an optional expandable
// detail per row (family → scan titles) and native tooltips. Purely presentational:
// rows are precomputed by the helpers, selection lives in the page.
export function StatusBar({ open, closed, discarded }) {
  const total = open + closed + discarded;
  if (total === 0) {
    return <div className='h-2 rounded-full bg-surface-neutral overflow-hidden' />;
  }
  const openPct = (open / total) * 100;
  const closedPct = (closed / total) * 100;
  const discardedPct = (discarded / total) * 100;
  return (
    <div
      className='h-2 rounded-full overflow-hidden flex bg-surface-neutral'
      title={`open ${open} · closed ${closed} · discarded ${discarded}`}>
      {closedPct > 0 && <div style={{ width: `${closedPct}%`, backgroundColor: "#1FAF5C" }} />}
      {discardedPct > 0 && <div style={{ width: `${discardedPct}%`, backgroundColor: "#EF4444" }} />}
      {openPct > 0 && <div style={{ width: `${openPct}%`, backgroundColor: "#9CA3AF" }} />}
    </div>
  );
}

export const SORT_OPTIONS = [
  { id: "reject_desc", label: "Reject rate (worst first)" },
  { id: "reject_asc", label: "Reject rate (best first)" },
  { id: "done_desc", label: "Confirmed done (high → low)" },
  { id: "volume_desc", label: "Volume (most issues)" },
  { id: "name_asc", label: "Name (A → Z)" },
];

export default function RankList({
  title,
  subtitle,
  emptyMsg,
  rows,
  rowKey,
  rowName,
  rowSearchText, // optional (r) => string; defaults to rowName
  rowMeta, // optional (r) => node, rendered under the counts line
  rowTooltip, // optional (r) => string, native title on the row
  primaryMetric, // (r) => { value: number|null, label: string, sub?: string }
  defaultSortId = "reject_desc",
  selected,
  onSelect,
  renderExpanded, // optional (r) => node; rows with canExpand(r) get a chevron
  canExpand = () => false,
  footnote,
}) {
  const [search, setSearch] = useState("");
  const [sortId, setSortId] = useState(defaultSortId);
  const [expanded, setExpanded] = useState(() => new Set());

  // A selected row is the one being analysed: open it.
  useEffect(() => {
    if (!selected) return;
    setExpanded((prev) => (prev.has(selected) ? prev : new Set([...prev, selected])));
  }, [selected]);

  const visibleRows = useMemo(() => {
    let list = rows || [];
    if (search) {
      const term = search.toLowerCase();
      const text = rowSearchText || rowName;
      list = list.filter((r) => text(r).toLowerCase().includes(term));
    }
    return sortRows(list, sortId, rowName);
  }, [rows, search, sortId, rowName, rowSearchText]);

  const toggleExpanded = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <div className='bg-white border border-border-subtle rounded-2xl p-4 flex flex-col' data-testid={`ranklist-${title}`}>
      <div className='flex items-start justify-between gap-2 mb-1'>
        <div className='min-w-0'>
          <h2 className='text-h3 text-text-primary'>{title}</h2>
          <p className='text-body-xs text-text-secondary'>{subtitle}</p>
        </div>
      </div>
      <div className='flex items-center gap-2 mb-3 mt-2'>
        <input
          type='text'
          placeholder='Search…'
          aria-label={`Search ${title}`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className='flex-1 px-3 py-1.5 text-body-xs bg-white border border-border-default rounded-lg text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-purple/20'
        />
        <select
          value={sortId}
          aria-label={`Sort ${title}`}
          onChange={(e) => setSortId(e.target.value)}
          className='px-2 py-1.5 text-body-xs bg-white border border-border-default rounded-lg text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-purple/20 max-w-[180px]'>
          {SORT_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {visibleRows.length === 0 ? (
        <p className='text-text-tertiary text-body-xs'>{emptyMsg}</p>
      ) : (
        <div className='space-y-1 max-h-[460px] overflow-auto pr-1'>
          {visibleRows.map((r) => {
            const key = rowKey(r);
            const isSel = selected === key;
            const metric = primaryMetric(r);
            const expandable = Boolean(renderExpanded && canExpand(r));
            const isOpen = expandable && expanded.has(key);
            return (
              <div key={key} className={`rounded-lg ${isSel ? "bg-accent-purple/10 ring-1 ring-accent-purple/30" : ""}`}>
                <div className='flex items-stretch'>
                  <button
                    type='button'
                    data-testid='rank-row'
                    onClick={() => onSelect(r)}
                    title={rowTooltip ? rowTooltip(r) : undefined}
                    className={`flex-1 min-w-0 text-left p-2 rounded-lg transition-colors ${isSel ? "" : "hover:bg-surface-neutral"}`}>
                    <div className='flex items-center gap-3'>
                      <div className='flex-1 min-w-0'>
                        <p className='text-body-xs font-medium text-text-primary truncate'>{rowName(r)}</p>
                        <p className='text-caption text-text-tertiary truncate'>
                          <span className='text-accent-green'>{r.closed.toLocaleString()} closed</span>
                          {" · "}
                          <span className='text-severity-medium'>{r.discarded.toLocaleString()} discarded</span>
                          {" · "}
                          <span>{r.open.toLocaleString()} open</span>
                        </p>
                        {rowMeta && rowMeta(r)}
                      </div>
                      <div className='w-16 text-right shrink-0'>
                        <p className='text-body-xs font-semibold text-text-primary'>
                          {metric.value == null ? <span className='text-text-tertiary'>n/a</span> : metric.label}
                        </p>
                        <p className='text-caption text-text-tertiary whitespace-nowrap'>{metric.sub || `${r.total.toLocaleString()} total`}</p>
                      </div>
                    </div>
                    <div className='mt-1.5'>
                      <StatusBar open={r.open} closed={r.closed} discarded={r.discarded} />
                    </div>
                  </button>
                  {expandable && (
                    <button
                      type='button'
                      aria-label={`${isOpen ? "Collapse" : "Expand"} ${rowName(r)}`}
                      aria-expanded={isOpen}
                      onClick={() => toggleExpanded(key)}
                      className='px-2 text-text-tertiary hover:text-text-primary rounded-lg hover:bg-surface-neutral'>
                      <svg
                        className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                      </svg>
                    </button>
                  )}
                </div>
                {isOpen && <div className='px-2 pb-2'>{renderExpanded(r)}</div>}
              </div>
            );
          })}
        </div>
      )}
      {footnote && <p className='text-caption text-text-tertiary mt-2'>{footnote}</p>}
    </div>
  );
}
